import { IApplicationContext } from '~/common/types';

type ThemeColorField =
  | 'platformBackgroundColor'
  | 'primaryColor'
  | 'menuBackgroundColor'
  | 'textColorActive'
  | 'subtitleColor'
  | 'textColorInactive'
  | 'primaryTextColor';

export type IThemeColors = Partial<Record<ThemeColorField, string | null>>;

const THEME_COLOR_FIELDS: ThemeColorField[] = [
  'platformBackgroundColor',
  'primaryColor',
  'menuBackgroundColor',
  'textColorActive',
  'subtitleColor',
  'textColorInactive',
  'primaryTextColor',
];

export const THEME_CSS_VARIABLES: ReadonlyArray<
  readonly [cssVariable: string, field: ThemeColorField]
> = [
  // bg
  ['--falcon-body-bg', 'platformBackgroundColor'],
  ['--falcon-bg-navbar-glass', 'platformBackgroundColor'],
  // primary color: button bg
  ['--falcon-primary', 'primaryColor'],
  ['--primary-color', 'primaryColor'],
  // side menu bg
  ['--menu-bg-color', 'menuBackgroundColor'],
  // title text
  ['--title-text-color', 'textColorActive'],
  // description text
  ['--paragraph-text-color', 'subtitleColor'],
  // menu item color
  ['--menu-item-text-color', 'textColorInactive'],
  // menu item color hover/active
  ['--menu-item-hover-color', 'primaryTextColor'],
  // primary button text color
  ['--primary-btn-color', 'primaryTextColor'],
  // wizard active text color
  ['--wizard-active-color', 'primaryTextColor'],
];

// an empty color takes its default, or is removed so the element inherits it
export const applyThemeTo = (
  element: HTMLElement,
  theme: IThemeColors,
  defaults: Partial<Record<string, string>> = {},
) => {
  for (const [cssVariable, field] of THEME_CSS_VARIABLES) {
    element.style.setProperty(
      cssVariable,
      String(theme[field] || defaults[cssVariable] || ''),
    );
  }
};

export const clearThemeFrom = (element: HTMLElement) => {
  for (const [cssVariable] of THEME_CSS_VARIABLES) {
    element.style.removeProperty(cssVariable);
  }
};

// the stylesheet value of each theme variable, read on :root with the applied theme lifted for
// the read, then put back; no paint happens in between
export const readStylesheetTheme = (): Record<string, string> => {
  const rootElement = document.documentElement;
  const appliedTheme = THEME_CSS_VARIABLES.map(
    ([cssVariable]) =>
      [cssVariable, rootElement.style.getPropertyValue(cssVariable)] as const,
  );
  clearThemeFrom(rootElement);
  const computedStyle = getComputedStyle(rootElement);
  const stylesheetTheme = Object.fromEntries(
    THEME_CSS_VARIABLES.map(([cssVariable]) => [
      cssVariable,
      computedStyle.getPropertyValue(cssVariable).trim(),
    ]),
  );
  for (const [cssVariable, value] of appliedTheme) {
    if (value) {
      rootElement.style.setProperty(cssVariable, value);
    }
  }

  return stylesheetTheme;
};

// same rule as the backend ThemeService: an empty criteria color falls back to the global one
export const mergeThemes = (
  globalTheme: IThemeColors,
  criteriaTheme: IThemeColors,
): IThemeColors =>
  Object.fromEntries(
    THEME_COLOR_FIELDS.map((field) => [
      field,
      criteriaTheme[field] || globalTheme[field],
    ]),
  );

export const applyTheme = (userTheme: IApplicationContext['userTheme']) => {
  const rootElement = document.querySelector(':root') as HTMLElement;
  if (rootElement) {
    applyThemeTo(rootElement, userTheme);
  }
};
